'use client';

import { Tooltip as ArkTooltip } from '@ark-ui/react/tooltip';
import { cx } from '@repo/panda/css';
import { type ButtonVariantProps, button, type IconButtonVariantProps, iconButton } from '@repo/panda/recipes';
import type React from 'react';
import { useId } from 'react';
import { DEFAULT_TOOLTIP_Z_INDEX } from './tooltip/constants';
import { TooltipPopup } from './tooltip/tooltip-popup';
import type { AccessibleName } from './types';

type BaseProps = Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'color' | 'className' | 'children'> & {
    'aria-disabled'?: never;
    /**
     * Why the button is disabled. Applies only with `disabled`: the button then stays focusable, ignores
     * activation, and shows the reason as a tooltip on hover or focus. It looks like any disabled button.
     *
     * Pass it whether or not the button is disabled (`disabledReason={t('no_boosts')}`, not
     * `disabled ? reason : undefined`): adding or removing it remounts the button and drops focus. Don't
     * also wrap the button in a `Tooltip`; only one tooltip can be open at a time.
     */
    disabledReason?: string;
    className?: string;
    children: React.ReactNode;
    ref?: React.Ref<HTMLButtonElement>;
};

export type ButtonProps = BaseProps &
    ButtonVariantProps & {
        /** The label, with any `<Icon decorative />` in reading order. Icon-only buttons are `IconButton`. */
        children: React.ReactNode;
    };

/** Wrappers must narrow this with `DistributiveOmit` (`@repo/panda/types`); a plain `Omit` drops the name requirement. */
export type IconButtonProps = Omit<BaseProps, 'title' | 'aria-label' | 'aria-labelledby'> &
    IconButtonVariantProps &
    AccessibleName;

// While a disabled button explains itself it has to stay focusable, so instead of native `disabled` it
// drops every handler that could activate it (including ones merged in by an Ark trigger) and keeps only
// those that let the tooltip open.
const keptWhileDisabled = /^on(Focus|Blur|PointerEnter|PointerLeave|PointerMove|MouseEnter|MouseLeave|MouseMove)$/;

const withoutActivation = <T extends object>(props: T): T =>
    Object.fromEntries(
        Object.entries(props).filter(([key]) => !/^on[A-Z]/.test(key) || keptWhileDisabled.test(key)),
    ) as T;

const blockActivation = (event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
};

const ButtonBase = ({
    recipeClass,
    disabled,
    disabledReason,
    type = 'button',
    className,
    children,
    ref,
    ...rest
}: BaseProps & { recipeClass: string }) => {
    const reasonId = useId();
    const explained = Boolean(disabled) && Boolean(disabledReason);
    const describedBy = [rest['aria-describedby'], explained ? reasonId : undefined].filter(Boolean).join(' ');

    const element = (
        <button
            {...(explained ? withoutActivation(rest) : rest)}
            ref={ref}
            type={type}
            disabled={explained ? undefined : disabled}
            aria-disabled={explained || undefined}
            aria-describedby={describedBy || undefined}
            onClick={explained ? blockActivation : rest.onClick}
            className={cx(recipeClass, className)}
        >
            {children}
        </button>
    );

    // Mounted whenever the prop is passed, rather than only while disabled, so toggling `disabled` never
    // remounts the button and loses focus.
    if (disabledReason === undefined) {
        return element;
    }

    return (
        <ArkTooltip.Root
            disabled={!explained}
            // an id from the consumer or an outer Ark trigger replaces Ark's on the button; zag finds the anchor by it
            ids={rest.id ? { trigger: rest.id } : undefined}
            positioning={{ placement: 'top', gutter: 8 }}
            openDelay={500}
            closeDelay={0}
            closeOnPointerDown={false}
            lazyMount
            unmountOnExit
        >
            <ArkTooltip.Trigger asChild>{element}</ArkTooltip.Trigger>
            {explained && (
                <span id={reasonId} hidden>
                    {disabledReason}
                </span>
            )}
            <TooltipPopup content={disabledReason} size="md" showArrow zIndex={DEFAULT_TOOLTIP_Z_INDEX} />
        </ArkTooltip.Root>
    );
};

export const Button = ({ variant, color, size, radius, fullWidth, ...props }: ButtonProps) => (
    <ButtonBase {...props} recipeClass={button({ variant, color, size, radius, fullWidth })} />
);

export const IconButton = ({ variant, color, size, ...props }: IconButtonProps) => (
    <ButtonBase {...props} recipeClass={iconButton({ variant, color, size })} />
);
